import AddNewTagModal from '@features/applications/components/AddNewTagModal/AddNewTagModal';
import DeleteTagModal from '@features/applications/components/DeleteTagModal/DeleteTagModal';
import { Delete, Edit } from '@mui/icons-material';
import { IconButton, TableCell, TableRow, Button } from '@mui/material';
import { FC, useContext, useState } from 'react';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { darkBlue } from '@styles/colors';
import { useNavigate } from 'react-router-dom';
import { authContext } from '@staffedupusa/shared-components';
import DeleteEmployerTagModal from '@features/applications/components/DeleteEmployerTagModal/DeleteEmployerTagModal';
import ConfirmationModal from '@features/enterprise/components/ConfirmationModal';

type Tag = {
  id: number;
  tag: string;
  total_assigned_application: number;
  predefined: number;
};

type TagRowProps = {
  tag: Tag;
  selectedCompanyId?: number | string;
  templateId?: number;
  isAppliedChanges?: boolean;
};
const TagRow: FC<TagRowProps> = ({
  tag,
  selectedCompanyId,
  templateId,
  isAppliedChanges,
}) => {
  const { authData } = useContext(authContext);
  const navigate = useNavigate();
  const [editableTag, setEditableTag] = useState<Tag | null>(null);
  const [deleteTag, setDeleteTag] = useState<Tag | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [pendingUpdate, setPendingUpdate] = useState<Tag | null>(null);
  const [checkDelete, setCheckDelete] = useState(false);

  const handleEditTag = (tag: Tag) => {
    if (isAppliedChanges && templateId) {
      setPendingUpdate(tag);
      setIsConfirmOpen(true);
    } else {
      setEditableTag(tag);
    }
  };

  const handleDeleteTag = (tag: Tag) => {
    if (isAppliedChanges && templateId) {
      setPendingUpdate(tag);
      setCheckDelete(true);
      setIsConfirmOpen(true);
    } else {
      setDeleteTag(tag);
    }
  };

  const handleConfirm = () => {
    if (checkDelete) {
      setDeleteTag(pendingUpdate);
      setCheckDelete(false);
    } else {
      setEditableTag(pendingUpdate);
    }
    setIsConfirmOpen(false);
  };
  const handleCancel = () => {
    setPendingUpdate(null);
    setCheckDelete(false);
    setIsConfirmOpen(false);
  };

  const handleNavigate = () => {
    navigate(
      authData?.user?.company
        ? `/dashboard?selected-tag=${tag.id}`
        : `/enterprise?selected-tag=${tag.id}&selected-company=${selectedCompanyId}`,
    );
  };

  return (
    <>
      <TableRow key={tag.id}>
        <TableCell>
          {templateId ? (
            tag.tag
          ) : (
            <Button
              variant="text"
              size="small"
              startIcon={<LocalOfferIcon />}
              sx={{
                textTransform: 'none',
                textDecoration: 'underline',
                color: darkBlue,
              }}
              onClick={handleNavigate}
            >
              {tag.tag}
            </Button>
          )}
        </TableCell>
        {!templateId && (
          <TableCell>
            <Button
              variant="text"
              size="small"
              sx={{
                textTransform: 'none',
                textDecoration: 'underline',
                color: darkBlue,
              }}
              onClick={handleNavigate}
            >
              {tag.total_assigned_application}
            </Button>
          </TableCell>
        )}
        <TableCell>
          <IconButton
            color="primary"
            onClick={() => handleEditTag(tag)}
            disabled={tag.predefined === 1}
            id={`edit-tag-button-${tag.tag.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <Edit />
          </IconButton>
          <IconButton
            color="error"
            onClick={() => handleDeleteTag(tag)}
            disabled={tag.predefined === 1}
            id={`delete-tag-button-${tag.tag.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <Delete />
          </IconButton>
        </TableCell>
      </TableRow>

      {editableTag && (
        <AddNewTagModal
          handleClose={() => {
            setEditableTag(null);
          }}
          editable={editableTag ?? null}
          selectedCompanyId={selectedCompanyId}
          templateId={templateId}
        />
      )}

      {deleteTag && (
        <>
          {authData?.user?.enterprise ? (
            <DeleteTagModal
              handleClose={() => setDeleteTag(null)}
              deleteTag={deleteTag}
              selectedCompanyId={selectedCompanyId}
              templateId={templateId}
            />
          ) : (
            <DeleteEmployerTagModal
              handleClose={() => setDeleteTag(null)}
              deleteTag={deleteTag}
            />
          )}
        </>
      )}
      <ConfirmationModal
        open={isConfirmOpen}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </>
  );
};

export default TagRow;
